import { Component } from 'preact';
import {
  GOLD,
  ACCENT_GOLD,
  TEXT_BODY,
  TEXT_PRIMARY,
  TEXT_MUTED,
  TEXTURED_PANEL_STYLE,
  SUB_PANEL_STYLE,
  CONTENT_BG_DEFAULT,
  FourCorners,
  GOLD_BTN,
} from '../../lib/medievalTheme';
import {
  MANUAL_BUFF_DURATION,
  N_TURNS_MIN,
  N_TURNS_MAX,
  formatBuffExpiry,
} from '../../utils/game/engine/automation';
import { OP } from '../../utils/game/engine/automation/schema';
import { pushDismiss, removeDismiss } from '../../utils/escStack';
import { k, t } from '../../localization/t';

const OPAQUE_PANEL_BG = '#0e0c08';
const OPAQUE_PANEL_STYLE = {
  ...TEXTURED_PANEL_STYLE,
  background: `${CONTENT_BG_DEFAULT}, ${OPAQUE_PANEL_BG}`,
};
const OPAQUE_SUB_PANEL_STYLE = {
  ...SUB_PANEL_STYLE,
  background: `${CONTENT_BG_DEFAULT}, ${OPAQUE_PANEL_BG}`,
};

const AMOUNT_MIN = -9;
const AMOUNT_MAX = 9;

const PANEL_WIDTH  = '24cqw';
const PANEL_HEIGHT = '26cqw';

const DURATION_OPTIONS = [
  { value: MANUAL_BUFF_DURATION.THIS_TURN, label: k('Current turn'),    tooltip: k('Until end of the current turn') },
  { value: MANUAL_BUFF_DURATION.NEXT_TURN, label: k('Your next turn'),  tooltip: k('Until end of your next turn') },
  { value: MANUAL_BUFF_DURATION.N_TURNS,   label: k('N turns'),         tooltip: k('For N of your turns'), hasCounter: true },
  { value: MANUAL_BUFF_DURATION.PERMANENT, label: k('Permanent'),       tooltip: k('Until you remove it') },
];

export class HudBuffPanel extends Component {
  state = {
    amount: 1,
    duration: MANUAL_BUFF_DURATION.THIS_TURN,
    nTurns: 2,
  };

  componentDidMount() {
    this._escDismissId = pushDismiss(() => this.props.onClose?.());
  }

  componentWillUnmount() {
    removeDismiss(this._escDismissId);
  }

  _stepAmount = (sign) => {
    this.setState((s) => {
      let next = s.amount + sign;
      if (next === 0) next = sign;
      return { amount: Math.max(AMOUNT_MIN, Math.min(AMOUNT_MAX, next)) };
    });
  };

  _onAmountInput = (e) => {
    const v = parseInt(e.currentTarget.value, 10);
    if (Number.isNaN(v)) return;
    this.setState({ amount: Math.max(AMOUNT_MIN, Math.min(AMOUNT_MAX, v)) });
  };

  _stepNTurns = (sign) => {
    // Stepping the counter is a distinct intent from selecting the
    // N-turns option. Only adjust the value; the user picks the
    // duration by clicking the tile body separately.
    this.setState((s) => ({
      nTurns: Math.max(N_TURNS_MIN, Math.min(N_TURNS_MAX, s.nTurns + sign)),
    }));
  };

  _apply = () => {
    if (this.state.amount === 0) return;
    this.props.onAddBuff?.({
      amount: this.state.amount,
      duration: this.state.duration,
      n: this.state.nTurns,
    });
    this.setState({ amount: 1, duration: MANUAL_BUFF_DURATION.THIS_TURN, nTurns: 2 });
  };

  _getManaEntries = () => {
    const { effectLedger, player } = this.props;
    return effectLedger.entries
      .filter((e) => e.controller === player && e.effect?.op === OP.GRANT_MANA)
      .slice()
      .reverse();
  };

  _resolveSourceName = (entry) => {
    if (entry.isManual) return t('Mana buff');
    if (entry.sourceCardName) return entry.sourceCardName;
    const { sorceryCards } = this.props;
    const card = sorceryCards?.find((c) => c.unique_id === entry.sourceCardId);
    return card?.name || t('Unknown card');
  };

  _positionStyle = () => {
    const { canvasLocalPos } = this.props;
    if (!canvasLocalPos) return null;
    if (canvasLocalPos.edge === 'bottom') {
      // Bottom-aligned: panel's bottom edge matches the HUD's bottom
      // edge; panel grows UP.
      return {
        bottom: `${canvasLocalPos.distFromCanvasBottom}px`,
        top: 'auto',
      };
    }
    return {
      top: `${canvasLocalPos.top}px`,
      bottom: 'auto',
    };
  };

  render({ player, viewerPlayer, canEdit, effectLedger, canvasLocalPos, onRemoveBuff }) {
    if (!canvasLocalPos) return null;
    // Driven by the caller's authoritative gate (spectator, opponent
    // in MP, etc.) rather than re-derived here from viewerPlayer ===
    // player, which doesn't account for spectators who have a default
    // viewerPlayer that may match one of the seats.
    const isReadOnly = !canEdit;
    const entries = this._getManaEntries();
    const { amount, duration, nTurns } = this.state;
    const canApply = amount !== 0;
    const sign = amount > 0 ? `+${amount}` : `${amount}`;
    const amountColor = amount > 0 ? '#7ec8ff' : amount < 0 ? '#ff8a78' : TEXT_PRIMARY;

    return (
      <div
        className="pointer-events-auto flex flex-col"
        style={{
          position: 'fixed',
          left: `${canvasLocalPos.left}px`,
          ...this._positionStyle(),
          transform: 'translateX(-100%)',
          marginLeft: '-0.5cqw',
          zIndex: 70,
          width: PANEL_WIDTH,
          height: PANEL_HEIGHT,
          padding: '0.9cqw',
          gap: '0.7cqw',
          ...OPAQUE_PANEL_STYLE,
        }}
        data-buff-zone="panel"
        onClick={(e) => e.stopPropagation()}
      >
        <FourCorners color={`${GOLD} 0.55)`} />

        {/* Active buffs. Takes the full upper area, scrollable when full. */}
        <div
          className="flex-1 overflow-y-auto"
          style={{
            ...OPAQUE_SUB_PANEL_STYLE,
            minHeight: 0,
            padding: '0.4cqw',
          }}
        >
          {entries.length === 0 ? (
            <div
              className="flex items-center justify-center w-full h-full"
              style={{ color: TEXT_MUTED, fontSize: '0.78cqw', textAlign: 'center', fontStyle: 'italic' }}
            >
              {t('No active mana buffs')}
            </div>
          ) : entries.map((entry) => {
            const amt = entry.effect?.args?.amount ?? 0;
            const amtSign = amt > 0 ? `+${amt}` : `${amt}`;
            const amtColor = amt > 0 ? '#7ec8ff' : '#ff8a78';
            return (
              <div
                key={entry.id}
                className="flex items-center"
                style={{
                  gap: '0.4cqw',
                  padding: '0.4cqw 0.5cqw',
                  borderRadius: 'var(--radius-sm)',
                  marginBottom: '0.25cqw',
                  background: 'rgba(0,0,0,0.28)',
                  border: `1px solid ${GOLD} 0.14)`,
                  fontSize: '0.78cqw',
                  color: TEXT_PRIMARY,
                }}
              >
                {!entry.isManual ? (
                  <span title={t('From card automation')} style={{ color: ACCENT_GOLD, fontSize: '0.78cqw', lineHeight: 1 }}>▣</span>
                ) : (
                  <span style={{ width: '0.78cqw', display: 'inline-block', flexShrink: 0 }} />
                )}
                <span className="flex-1 truncate" title={this._resolveSourceName(entry)}>
                  {this._resolveSourceName(entry)}
                </span>
                <span
                  className="tabular-nums"
                  style={{ color: amtColor, fontWeight: 800, minWidth: '1.6cqw', textAlign: 'right' }}
                >
                  {amtSign}
                </span>
                <span style={{ color: TEXT_MUTED, fontSize: '0.65cqw', whiteSpace: 'nowrap' }}>
                  {formatBuffExpiry(entry, viewerPlayer)}
                </span>
                {!isReadOnly ? (
                  <button
                    type="button"
                    className="transition-colors"
                    onClick={() => onRemoveBuff?.(entry.id)}
                    title={t('Remove this buff')}
                    style={{
                      width: '1.2cqw',
                      height: '1.2cqw',
                      borderRadius: 'var(--radius-sm)',
                      background: 'rgba(0,0,0,0.4)',
                      border: `1px solid ${GOLD} 0.25)`,
 color: TEXT_BODY,
 fontSize: '0.85cqw',
 lineHeight: 1,
 display: 'flex',
 alignItems: 'center',
 justifyContent: 'center',
 flexShrink: 0,
 }}
 onMouseEnter={(e) => {
 e.currentTarget.style.background = 'rgba(180,60,60,0.25)';
 e.currentTarget.style.color = '#ff8a78';
 }}
 onMouseLeave={(e) => {
 e.currentTarget.style.background = 'rgba(0,0,0,0.4)';
 e.currentTarget.style.color = TEXT_BODY;
 }}
 aria-label={t('Remove buff')}
 >×</button>
 ) : null}
 </div>
 );
 })}
 </div>

 {/* Creation surface. 2x2 duration grid, then a single stepper-
 plus-apply row. Tighter row heights reclaim space for the
 buffs list above. */}
 {!isReadOnly ? (
 <div
 style={{
 ...OPAQUE_SUB_PANEL_STYLE,
 padding: '0.6cqw',
 display: 'flex',
 flexDirection: 'column',
 gap: '0.5cqw',
 flexShrink: 0,
 }}
 >
 <div
 className="grid"
 style={{ gridTemplateColumns: '1fr 1fr', gap: '0.35cqw' }}
 >
 {DURATION_OPTIONS.map((opt) => (
 <DurationCard
 key={opt.value}
 option={opt}
 selected={duration === opt.value}
 nTurns={nTurns}
 onSelect={() => this.setState({ duration: opt.value })}
 onStepN={(s) => this._stepNTurns(s)}
 />
 ))}
 </div>

 <div className="flex items-center" style={{ gap: '0.5cqw' }}>
 <button
 type="button"
 className="arena-heading vh-medieval-btn-flat"
 onClick={this._apply}
 disabled={!canApply}
 style={{
 ...GOLD_BTN,
 flex: 1,
 padding: '0.35cqw',
 borderRadius: 'var(--radius-sm)',
 fontSize: '0.75cqw',
 opacity: canApply ? 1 : 0.4,
 }}
 >
 {canApply ? t('Apply {sign}', { sign }) : t('Apply')}
 </button>
 <div
 className="flex items-center"
 style={{
 gap: '0.05cqw',
 flexShrink: 0,
 background: 'rgba(0,0,0,0.55)',
 border: `1px solid ${GOLD} 0.32)`,
                  borderRadius: 'var(--radius-full)',
                  padding: '0.12cqw 0.18cqw',
                  boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.4)',
                }}
              >
                <PillBtn onClick={() => this._stepAmount(-1)} label="−" />
                <input
                  type="number"
                  value={amount}
                  onInput={this._onAmountInput}
                  className="text-center tabular-nums"
                  style={{
                    width: '1.7cqw',
                    height: '1.4cqw',
                    background: 'transparent',
                    border: 'none',
                    color: amountColor,
                    fontWeight: 800,
                    fontSize: '0.9cqw',
                    outline: 'none',
                    padding: 0,
                    appearance: 'textfield',
                    MozAppearance: 'textfield',
                  }}
                />
                <PillBtn onClick={() => this._stepAmount(1)} label="+" />
              </div>
              <span style={{ color: TEXT_BODY, fontSize: '0.72cqw' }}>{t('mana')}</span>
            </div>
          </div>
        ) : null}
      </div>
    );
  }
}

// One duration tile. Uses div role="button" instead of a real <button>
// because the N-turns tile contains nested <button> elements for the
// counter. Nested buttons are invalid HTML and trigger the browser's
// focus/highlight quirk where stepping the counter would activate the
// parent. With a div outer, the tile selects only on its own onClick.
function DurationCard({ option, selected, nTurns, onSelect, onStepN }) {
  const borderColor = selected ? `${GOLD} 0.65)` : `${GOLD} 0.18)`;
  const bg = selected ? `${GOLD} 0.18)` : 'rgba(0,0,0,0.32)';
  const shadow = selected
    ? `0 0 12px ${GOLD} 0.22), inset 0 0 0 1px ${GOLD} 0.35)`
    : 'inset 0 1px 0 rgba(255,255,255,0.02)';
  const titleColor = selected ? TEXT_PRIMARY : TEXT_BODY;

  const labelStyle = {
    fontSize: '0.75cqw',
    letterSpacing: '0.08em',
    lineHeight: 1.1,
    fontWeight: 700,
    textShadow: selected ? '0 1px 2px rgba(0,0,0,0.5)' : 'none',
  };

  return (
    <div
      role="button"
      tabIndex={-1}
      onClick={onSelect}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(); } }}
      title={t(option.tooltip)}
      className="text-center flex items-center justify-center transition-colors"
      style={{
        padding: '0 0.4cqw',
        height: '1.95cqw',
        borderRadius: 'var(--radius-sm)',
        background: bg,
        border: `1px solid ${borderColor}`,
        boxShadow: shadow,
        color: titleColor,
        gap: '0.4cqw',
        userSelect: 'none',
        outline: 'none',
      }}
      onMouseEnter={(e) => {
        if (selected) return;
        e.currentTarget.style.background = `${GOLD} 0.08)`;
        e.currentTarget.style.borderColor = `${GOLD} 0.35)`;
      }}
      onMouseLeave={(e) => {
        if (selected) return;
        e.currentTarget.style.background = bg;
        e.currentTarget.style.borderColor = borderColor;
      }}
    >
      {option.hasCounter ? (
        <>
          <CounterBtn onClick={() => onStepN(-1)} label="−" />
          <span className="arena-heading flex-1" style={labelStyle}>
            {nTurns === 1 ? t('1 turn') : t('{n} turns', { n: nTurns })}
          </span>
          <CounterBtn onClick={() => onStepN(1)} label="+" />
        </>
      ) : (
        <span className="arena-heading" style={labelStyle}>{t(option.label)}</span>
      )}
    </div>
  );
}

function CounterBtn({ onClick, label }) {
  // stopPropagation prevents the parent tile's onClick from also firing
  // when the user clicks +/−. The parent is a div role="button", which
  // makes nesting these real buttons valid HTML (unlike a real outer
  // <button>, where the nested click was the source of the auto-select
  // / focus bug).
  return (
    <button
      type="button"
      className="vh-medieval-btn"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      onMouseDown={(e) => e.stopPropagation()}
      style={{
        ...GOLD_BTN,
        width: '1.2cqw',
        height: '1.2cqw',
        borderRadius: 'var(--radius-sm)',
        fontSize: '0.78cqw',
        fontWeight: 800,
        padding: 0,
        lineHeight: 1,
        flexShrink: 0,
      }}
      aria-label={label === '+' ? t('Increase turns') : t('Decrease turns')}
    >
      {label}
    </button>
  );
}

// Flat text-only button used inside the pill-shaped mana stepper. No
// gold-plate background. The pill itself provides the container, and
// the button is just a hot region with a hover tint. Visually distinct
// from the GOLD_BTN counter buttons in the duration tile.
function PillBtn({ onClick, label }) {
  const reset = (el) => {
    el.style.background = 'transparent';
    el.style.transform = 'scale(1)';
  };
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: 'transparent',
        border: 'none',
        color: ACCENT_GOLD,
        fontSize: '1cqw',
        fontWeight: 800,
        width: '1.4cqw',
        height: '1.4cqw',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        lineHeight: 1,
        transition: 'background-color 80ms ease-out, transform 80ms ease-out',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.18)`; }}
      onMouseLeave={(e) => reset(e.currentTarget)}
      onMouseDown={(e) => {
        e.currentTarget.style.background = `${GOLD} 0.32)`;
        e.currentTarget.style.transform = 'scale(0.92)';
      }}
      onMouseUp={(e) => { e.currentTarget.style.background = `${GOLD} 0.18)`; e.currentTarget.style.transform = 'scale(1)'; }}
      aria-label={label === '+' ? t('Increase') : t('Decrease')}
    >
      {label}
    </button>
  );
}
